import { DevicesAccess_GetUsersListDocument } from '@/__generated__/graphql';
import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import { ROW_HOVER_BG } from '@features/reports2/colors';
import { StepProps } from '@features/reports2/types';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { GridOn, PersonAdd, PictureAsPdf } from '@mui/icons-material';
import {
  Autocomplete,
  Avatar,
  Box,
  ButtonBase,
  Checkbox,
  Chip,
  FilterOptionsState,
  FormHelperText,
  InputBase,
  Skeleton,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { CSSProperties, FC, KeyboardEvent, Suspense, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useSuspenseQuery } from '@apollo/client';
import { List, RowComponentProps } from 'react-window';
import { CreateReportFieldValues } from '../../validation';
import { InnerPaper, StepContentBox, StepTitle } from '../common';

const CONTACT_ROW_HEIGHT = 52;

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const isValidEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

type Contact = { id: number; fullName: string; email: string };

const ADD_NEW_SENTINEL = -1;

const contactFilter = createFilterOptions<Contact>({ limit: 10 });

// ---------------------------------------------------------------------------
// Virtualized contact row (lista con checkboxes debajo del input)
// ---------------------------------------------------------------------------

type ContactRowProps = {
  contacts: Contact[];
  selectedEmails: Set<string>;
  toggleContact: (email: string) => void;
};

const ContactRow: FC<RowComponentProps<ContactRowProps>> = ({
  index,
  style,
  ariaAttributes,
  contacts,
  selectedEmails,
  toggleContact,
}) => {
  const user = contacts[index];
  const checked = selectedEmails.has(user.email);
  return (
    <div style={style as CSSProperties} {...ariaAttributes}>
      <ButtonBase
        onClick={() => toggleContact(user.email)}
        sx={{
          width: '100%',
          height: CONTACT_ROW_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          justifyContent: 'flex-start',
          ':hover': { bgcolor: ROW_HOVER_BG },
        }}
      >
        <Checkbox checked={checked} size="small" disableRipple />
        <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', flexShrink: 0 }}>
          {getInitials(user.fullName)}
        </Avatar>
        <Box textAlign="left" minWidth={0}>
          <Typography variant="body2" fontWeight="medium" lineHeight={1.3} noWrap>
            {user.fullName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {user.email}
          </Typography>
        </Box>
      </ButtonBase>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main step
// ---------------------------------------------------------------------------

const SuspenseComponent: FC<StepProps> = (props) => {
  const methods = useFormContext<CreateReportFieldValues>();
  const { _ } = useLingui();
  const [draft, setDraft] = useState('');

  const errors = methods.formState.errors.step3;
  const nextStepEnabled = !errors?.createPdf && !errors?.createExcel && !errors?.emails;

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'step3.emails',
  });

  const { data: usersData } = useSuspenseQuery(DevicesAccess_GetUsersListDocument);
  const contacts = usersData.users as Contact[];

  const selectedEmailsSet = new Set(fields.map((f) => f.email));

  const filterContacts = (options: Contact[], state: FilterOptionsState<Contact>) => {
    const filtered = contactFilter(options, {
      ...state,
      getOptionLabel: (o) => `${o.fullName} ${o.email}`,
    });
    const inputEmail = state.inputValue.trim();
    if (inputEmail.length > 0 && !options.some((o) => o.email === inputEmail) && !selectedEmailsSet.has(inputEmail)) {
      filtered.push({ id: ADD_NEW_SENTINEL, fullName: inputEmail, email: inputEmail });
    }
    return filtered;
  };

  const confirmDraft = (value: string) => {
    const email = value.trim().replace(/,+$/, '');
    if (email && isValidEmail(email) && !selectedEmailsSet.has(email)) {
      append({ email });
    }
    setDraft('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      return;
    }
    if (e.key === ',') {
      e.preventDefault();
      confirmDraft(draft);
    }
    if (e.key === 'Backspace' && draft === '' && fields.length > 0) {
      remove(fields.length - 1);
    }
  };

  const toggleContact = (email: string) => {
    const idx = fields.findIndex((f) => f.email === email);
    if (idx >= 0) remove(idx);
    else append({ email });
  };

  return (
    <StepContentBox
      actions={{
        ...props.actions,
        nextStepEnabled: props.actions.nextStepEnabled && nextStepEnabled,
        onNextStep: () => {
          methods.trigger('step3').then((success) => {
            if (success) props.actions.onNextStep();
          });
        },
      }}
    >
      <StepTitle
        title={<Trans>Formato y destinatarios</Trans>}
        description={<Trans>Elige el formato de envío y los destinatarios que desees.</Trans>}
      />

      {/* Formato */}
      <InnerPaper>
        <Typography fontWeight="bold" mb={2}>
          <Trans>Formato para el reporte</Trans>
        </Typography>
        <Box display="flex" gap={2}>
          {(
            [
              ['createPdf', <Trans>PDF</Trans>, PictureAsPdf],
              ['createExcel', <Trans>Excel</Trans>, GridOn],
            ] as const
          ).map(([value, label, Icon]) => (
            <Controller
              key={value}
              control={methods.control}
              name={`step3.${value}`}
              render={({ field }) => (
                <ButtonBase
                  onClick={() => {
                    field.onChange(!field.value);
                    methods.trigger('step3');
                  }}
                  sx={{
                    border: 1,
                    borderColor: field.value ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    gap: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Icon />
                  <Typography fontWeight="medium">{label}</Typography>
                  <Checkbox disableRipple checked={field.value} size="small" />
                </ButtonBase>
              )}
            />
          ))}
        </Box>
        {(errors?.createPdf || errors?.createExcel) && (
          <FormHelperText sx={{ mt: 1 }} error>
            <Trans>Debes seleccionar al menos un formato</Trans>
          </FormHelperText>
        )}
      </InnerPaper>

      {/* Correos + contactos */}
      <InnerPaper mt={2}>
        <Typography fontWeight="bold" mb={0.5}>
          <Trans>Correos de destino del reporte</Trans>
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          <Trans>Selecciona contactos guardados o agrega correos nuevos</Trans>
        </Typography>

        {/* Autocomplete freeSolo con chips */}
        <Autocomplete<Contact, false, false, true>
          freeSolo
          options={contacts}
          filterOptions={filterContacts}
          getOptionLabel={(o) => (typeof o === 'string' ? o : o.email)}
          inputValue={draft}
          onInputChange={(_, value, reason) => {
            if (reason !== 'reset') setDraft(value);
          }}
          onChange={(_, selected) => {
            if (!selected) return;
            const email = typeof selected === 'string' ? selected : selected.email;
            if (isValidEmail(email) && !selectedEmailsSet.has(email)) {
              append({ email });
            }
            setDraft('');
          }}
          renderOption={(optProps, option) => {
            if (option.id === ADD_NEW_SENTINEL) {
              return (
                <li {...optProps} key="add-new-contact">
                  <PersonAdd sx={{ mr: 1.5, fontSize: 20, color: 'primary.main', flexShrink: 0 }} />
                  <Box minWidth={0}>
                    <Typography variant="body2" fontWeight="medium">
                      <Trans>Agregar nuevo contacto</Trans>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {option.email}
                    </Typography>
                  </Box>
                </li>
              );
            }
            const checked = selectedEmailsSet.has(option.email);
            return (
              <li {...optProps} key={option.id}>
                <Checkbox checked={checked} size="small" disableRipple sx={{ mr: 1 }} />
                <Avatar sx={{ width: 28, height: 28, fontSize: '0.65rem', mr: 1.5, flexShrink: 0 }}>
                  {getInitials(option.fullName)}
                </Avatar>
                <Box minWidth={0}>
                  <Typography variant="body2" fontWeight="medium" noWrap>
                    {option.fullName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {option.email}
                  </Typography>
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <Box
              ref={params.InputProps.ref}
              onClick={() => document.getElementById('email-chip-input')?.focus()}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 0.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                px: 1.5,
                py: 1,
                cursor: 'text',
                minHeight: 46,
              }}
            >
              {fields.map((field, idx) => (
                <Chip
                  key={field.id}
                  label={field.email}
                  size="small"
                  onDelete={() => remove(idx)}
                />
              ))}
              <InputBase
                id="email-chip-input"
                inputProps={params.inputProps}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={() => confirmDraft(draft)}
                placeholder={
                  fields.length === 0 ? _(msg`Escribe un correo y presiona Enter`) : ''
                }
                sx={{ flex: 1, minWidth: 180, fontSize: '0.875rem' }}
              />
            </Box>
          )}
          getOptionDisabled={(option) => option.id === ADD_NEW_SENTINEL && !isValidEmail(option.email)}
          disableClearable={false}
          clearOnBlur={false}
        />
        {errors?.emails && (
          <FormHelperText error>
            <Trans>Debes indicar al menos 1 correo</Trans>
          </FormHelperText>
        )}

        {/* Lista con checkboxes (virtualizada) */}
        <Typography fontWeight="medium" mt={2} mb={0.5}>
          <Trans>Contactos guardados</Trans>
        </Typography>
        <Box sx={{ height: 'max(calc(78dvh - 520px), 140px)' }}>
          <List
            rowComponent={ContactRow}
            rowHeight={CONTACT_ROW_HEIGHT}
            rowCount={contacts.length}
            rowProps={{ contacts, selectedEmails: selectedEmailsSet, toggleContact }}
            style={{ scrollbarWidth: 'thin' }}
          />
        </Box>
      </InnerPaper>
    </StepContentBox>
  );
};

const ContactsSkeleton: FC = () => (
  <Box px={5} pt={2} pb={1} display="flex" flexDirection="column" gap={2}>
    <Skeleton variant="text" width={220} height={32} />
    <Skeleton variant="text" width={320} height={20} />
    <Skeleton variant="rounded" height={100} />
    <Skeleton variant="rounded" height={46} />
    <Skeleton variant="text" width={160} height={24} />
    <Skeleton variant="rounded" height="max(calc(78dvh - 520px), 140px)" />
  </Box>
);

export const FormatAndRecipientsStep: FC<StepProps> = (props) => {
  return (
    <ApolloErrorBoundary>
      <Suspense fallback={<ContactsSkeleton />}>
        <SuspenseComponent {...props} />
      </Suspense>
    </ApolloErrorBoundary>
  );
};
